import { defineCommand } from '../../../concepts/commands/CommandDefinition';
import { ObjectManager, component, property } from '../../../concepts/objects';
import { DateType } from '../../../concepts/objects/DateType';
import { memoUsing } from '../../../concepts/objects/MemoUsing';
import { Path } from '../../../platform/geom/Path';
import { Point } from '../../../platform/geom/Point';
import { Rect } from '../../../platform/geom/Rect';
import { AdapterInfo, CreateAdapterInfo } from '../../canvas/AdapterInfo';
import { EditorSession } from '../../canvas/EditorSession';
import { AutoBringToFront } from '../../canvas/entities/AutoBringToFront';
import { FixedRectangular } from '../../canvas/entities/FixedRectangular';
import { Physical, RelativeTo } from '../../canvas/entities/Physical';
import { UIField } from '../../canvas/entities/UIField';
import { ToolManager } from '../../canvas/tooling/ToolManager';
import { Audited } from '../auditing/Audited';
import { AutoLayoutSubject } from '../autoLayout/AutoLayout';
import { ConnectTarget } from '../connector/ConnectTarget';
import { Pingable } from '../presentation/Pingable';
import { TokenLayout } from './TokenLayout';
import { TokenValueField } from './TokenValueField';

const tokenMutate = defineCommand(
    'tokenMutate',
    null,
    (r, [id]: [string]) => {
        r.call(EditorSession).update(AdapterInfo, CreateAdapterInfo(id, 'field'));
    },
    [],
);

const repeat = defineCommand(
    'tokenRepeat',
    null,
    (r, [id]: [string]) => {
        const objects = r.call(ObjectManager);
        const token = objects.get(id, Token);
        const tools = r.call(ToolManager);
        tools.equip('tokenTool', {
            color: token.background,
            value: token.value,
        });
    },
);

const tokenDef = {
    name: 'Token',
    implements: [Physical, AutoBringToFront, AutoLayoutSubject, Audited, ConnectTarget, Pingable],
    mutation: tokenMutate
};

const tokenTransformHints = Object.freeze({
    scaleX: false,
    scaleY: false,
    rotate: false,
    translate: true,
});

const tokenSize = 84;

@component(tokenDef)
export class Token extends FixedRectangular implements AutoBringToFront, Audited {

    public static readonly Size = new Point(tokenSize, tokenSize);

    @property({ type: 'number', default: tokenSize })
    public width!: number;

    @property({ type: 'number', default: tokenSize })
    public height!: number;

    @property({ type: 'string', style: true })
    public background!: string;

    @property({ type: 'string', oldName: 'value|value' })
    public value!: string;

    @property({ type: 'boolean' })
    public concealed!: boolean;

    @property({ type: 'string' })
    public userId!: string;

    @property({ type: DateType })
    public created!: Date;

    public get boundary() {
        return true;
    }

    //
    // Physical
    //

    @memoUsing('children', 'width', 'height', 'value')
    public get layout() {
        return TokenLayout(this);
    }

    @memoUsing('layout')
    public get uiFields(): readonly UIField[] {
        return [TokenValueField(this)];
    }

    public queryTransformHints() {
        return tokenTransformHints;
    }

    public path(to?: RelativeTo | Physical): Path {
        return this.bounds(to).toOval();
    }

    public test(mpt: Point): boolean {
        const lpt = this.toRelative(mpt);
        const cp = new Point(this.width / 2, this.height / 2);
        return lpt.distanceTo(cp) <= ((tokenSize / 2) + 2);
    }

    protected setTransformationResult(x: number, y: number, r: number, w: number, h: number): void {
        const newBounds = new Rect(x, y, w, h);
        const newCenter = newBounds.center();

        w = tokenSize;
        h = tokenSize;
        x = newCenter.x - (w / 2);
        y = newCenter.y - (h / 2);
        r = 0;

        super.setTransformationResult(x, y, r, w, h);
    }
}
